import { createContext, useCallback, useContext, useMemo } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import usePreferencesQuery from 'api/queries/usePreferencesQuery';
import useProductsQuery from 'api/queries/useProductsQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import { FEATURE_FLAG_NAMES } from 'hooks/useFeatureFlag';
import { useAmplitudeFeatureFlag } from 'hooks/useFeatureFlag/useAmplitudeFeatureFlag';
import useUpdatePreference from 'hooks/useUpdatePreference';
import { trackEvent } from 'utils/analyticsUtils';
import { calculateAge } from 'utils/birthdayUtils';
import { getLoyaltyPointsBalance } from 'utils/shopUtils';

const ProductCardContext = createContext();

/**
 * Provider component that supplies product card related data and functionality to v2 <ProductCard /> component.
 *
 * @param {Object} props
 * @param {ReactNode} props.children - Child components that will have access to the ProductCard context
 * @returns {JSX.Element} Provider component wrapping children
 */
export function ProductCardProvider({ children }) {
    const { data: accountData = {} } = useAccountQuery();
    const { data: subscriptionData = {} } = useSubscriptionQuery();
    const { data: selectionsData = {} } = useSelectionsQuery();
    const { data: productsData = {} } = useProductsQuery();
    const { plusMemberPriceEligible, plusMembershipEligibleOrder, plusMembershipShortBranding } =
        useAccountPlusMembershipHook();
    const { data: preferenceData = {} } = usePreferencesQuery();

    const chargeID = accountData?.nextOrder?.chargeID;
    const isDeliveredByImperfect = accountData?.isDeliveredByImperfect;
    const prefCenterEligible = accountData?.prefCenterEligible;

    const subscriptionVariant = subscriptionData?.variant;

    const showMembershipPrice = plusMemberPriceEligible && plusMembershipEligibleOrder;
    const showMembershipPreviewPrice = plusMemberPriceEligible && !plusMembershipEligibleOrder;

    const ageIsUnder21 = calculateAge(subscriptionData?.dateOfBirth) < 21;

    const { isAssignedVariant: isRecurringEngagementFlagOn } = useAmplitudeFeatureFlag({
        flagName: FEATURE_FLAG_NAMES.RECURRING_ENGAGEMENT,
        variant: 'on',
    });

    const { addRecurringUserPreference, removeUserPreference } = useUpdatePreference();

    /**
     * Toggles a product's recurring status.
     *
     * @param {string} preferenceID - The external SKU of the product
     * @param {boolean} addToRecurring - Whether to add (true) or remove (false) from recurring
     * @param {Object} [trackingData] - Optional tracking data
     * @param {string} [trackingData.productName] - The product name for analytics
     * @param {boolean} [trackingData.soldOut] - Whether the product is sold out
     */
    const toggleRecurring = (preferenceID, addToRecurring, trackingData = {}) => {
        const { productName, soldOut } = trackingData;

        if (addToRecurring) {
            addRecurringUserPreference({ preferenceID });
        } else {
            removeUserPreference({ preferenceID });
        }

        // Track preference change if tracking data is provided
        if (productName !== undefined) {
            trackEvent('item preference update', {
                new_preference: addToRecurring ? 'recurring' : 'none',
                source: 'product card',
                path: window.location.pathname,
                isNative: !!window.isNativeApp,
                product_name: productName,
                product_id: preferenceID,
                out_of_stock: soldOut ?? false,
            });
        }
    };

    // Perks (AKA Shop with Points)
    const loyaltyPointsAvailable = accountData?.currentLoyaltyPoints;
    const loyaltyPointsBalance = getLoyaltyPointsBalance(loyaltyPointsAvailable, selectionsData);

    // Build a Set of recurring item external SKUs for fast lookups
    const recurringItemExternalSkus = useMemo(() => {
        const { recurring = [] } = preferenceData;
        const externalSkus = new Set();

        recurring[0]?.recurring?.forEach((item) => {
            externalSkus.add(item?.id);
        });

        return externalSkus;
    }, [preferenceData]);

    /**
     * Checks if a product is in the recurring item list.
     *
     * @param {string} externalSku - The external SKU of the product to check
     * @returns {boolean} True if the product is in the recurring item list, false otherwise
     */
    const isRecurring = useCallback(
        (externalSku) => recurringItemExternalSkus.has(externalSku),
        [recurringItemExternalSkus]
    );

    /**
     * Retrieves the cart selection data for a specific product ID from the selections data.
     * The selection data represents items in the cart and can come from either regular selections
     * or bundle selections. Returns empty object if no selection exists for the product.
     *
     * @param {string} productID - The ID of the product to find cart selection data for
     * @param {boolean} isPerk - Whether the product is a perk
     * @param {boolean} isGwp - Whether the product is a gift with purchase
     * @returns {Object} The cart selection data for the product if found, empty object otherwise
     */
    const getSelection = useCallback(
        (productID, isPerk = false, isGwp = false) => {
            const { selected = [], selectedBundles = [] } = selectionsData ?? {};

            if (selected.length === 0 && selectedBundles.length === 0) return {};

            const productSelectionData = [...selected, ...selectedBundles].find(
                (selection) =>
                    selection.id === productID && !!selection?.isPerk === isPerk && !!selection?.isGwpFree === isGwp
            );

            return productSelectionData;
        },
        [selectionsData]
    );

    // Gift with purchase - get selected items to check if any gift is in cart
    const { selected: selectedItems = [] } = selectionsData ?? {};

    const value = useMemo(
        () => ({
            ageIsUnder21,
            chargeID,
            getSelection,
            isDeliveredByImperfect,
            isRecurring,
            isRecurringEngagementFlagOn,
            loyaltyPointsAvailable,
            loyaltyPointsBalance,
            plusMemberPriceEligible,
            plusMembershipEligibleOrder,
            plusMembershipShortBranding,
            prefCenterEligible,
            productsData,
            selectedItems,
            showMembershipPrice,
            showMembershipPreviewPrice,
            subscriptionVariant,
            toggleRecurring,
        }),
        [
            ageIsUnder21,
            chargeID,
            getSelection,
            isDeliveredByImperfect,
            isRecurring,
            isRecurringEngagementFlagOn,
            loyaltyPointsAvailable,
            loyaltyPointsBalance,
            plusMemberPriceEligible,
            plusMembershipEligibleOrder,
            plusMembershipShortBranding,
            prefCenterEligible,
            productsData,
            selectedItems,
            showMembershipPrice,
            showMembershipPreviewPrice,
            subscriptionVariant,
            toggleRecurring,
        ]
    );

    return <ProductCardContext.Provider value={value}>{children}</ProductCardContext.Provider>;
}

export function useProductCardContext() {
    return useContext(ProductCardContext);
}

export default ProductCardContext;
